import { z } from 'zod'

import {
  VenueLayoutTableSize,
  VenueLayoutTableType,
} from '../constants/venueLayoutTable'

/** Embedded section in a V2 image floorplan draft (undo/autosave). */
export const floorplanImagePointSchema = z.object({
  xPos: z.number(),
  yPos: z.number(),
})

export const floorplanImageSectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  polygon: z.array(floorplanImagePointSchema).optional(),
  image: z.string().optional().nullable(),
})

/** Embedded table in a V2 image floorplan draft. */
export const floorplanImageTableSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.nativeEnum(VenueLayoutTableType),
  size: z.nativeEnum(VenueLayoutTableSize),
  xPos: z.number(),
  yPos: z.number(),
  capacity: z.number().int().min(0).optional().nullable(),
  venueLayoutSection: z.string().optional().nullable(),
  enable: z.boolean().optional().nullable(),
})

export const floorplanImageDocumentSchema = z.object({
  schemaVersion: z.literal(2),
  floorPlan: z.object({
    original: z.string(),
  }),
  sections: z.array(floorplanImageSectionSchema).default([]),
  tables: z.array(floorplanImageTableSchema).default([]),
})

export const createEmptyFloorplanImageDocument = (args: {
  floorPlanOriginal: string
}): z.infer<typeof floorplanImageDocumentSchema> => ({
  schemaVersion: 2,
  floorPlan: { original: args.floorPlanOriginal },
  sections: [],
  tables: [],
})

type ImageDocSectionInput = {
  id: string
  name: string
  polygon?: z.infer<typeof floorplanImagePointSchema>[]
  image?: string | null
  deleted?: boolean
}
type ImageDocTableInput = {
  id: string
  name: string
  type: z.infer<typeof floorplanImageTableSchema>['type']
  size: z.infer<typeof floorplanImageTableSchema>['size']
  xPos: number
  yPos: number
  capacity?: number | null
  venueLayoutSection?: string | null
  enable?: boolean | null
  deleted?: boolean
}

export function buildImageDocFromLayoutEntities(args: {
  floorPlanOriginal: string
  sections: ImageDocSectionInput[]
  tables: ImageDocTableInput[]
}): z.infer<typeof floorplanImageDocumentSchema> {
  return floorplanImageDocumentSchema.parse({
    schemaVersion: 2,
    floorPlan: { original: args.floorPlanOriginal },
    sections: args.sections
      .filter((section) => !section.deleted)
      .map((section) => ({
        id: section.id,
        name: section.name,
        polygon: section.polygon ?? [],
        image: section.image ?? null,
      })),
    tables: args.tables
      .filter((table) => !table.deleted)
      .map((table) => ({
        id: table.id,
        name: table.name,
        type: table.type,
        size: table.size,
        xPos: table.xPos,
        yPos: table.yPos,
        capacity: table.capacity ?? null,
        venueLayoutSection: table.venueLayoutSection ?? null,
        enable: table.enable ?? true,
      })),
  })
}
